import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  RemoveCircle as DismissIcon,
  Park as ParkIcon,
  Image as ImageIcon,
  EditLocationAlt as EditLocationIcon,
  Report as ReportIcon,
  ReportProblem as ReportProblemIcon,
  Store as StoreIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { normalizeImageUrl } from '../services/imageUtils';

const TYPE_LABELS = {
  GARDEN_REQUEST: 'Garden requests',
  GARDEN_REMOVAL_REPORT: 'Garden removal reports',
  PHOTO_SUGGESTION: 'Photo suggestions',
  GARDEN_LOCATION_SUGGESTION: 'Location suggestions',
  POST_REPORT: 'Post reports',
  PROVIDER_APPLICATION: 'Provider applications',
  PROVIDER_PUBLIC_APPLICATION: 'Public applications',
};

const TAB_DEFS = [
  { key: 'ALL', label: 'All', icon: <InboxIcon fontSize="small" />, type: null },
  { key: 'GARDEN_REQUEST', label: TYPE_LABELS.GARDEN_REQUEST, icon: <ParkIcon fontSize="small" />, type: 'GARDEN_REQUEST' },
  { key: 'GARDEN_REMOVAL_REPORT', label: TYPE_LABELS.GARDEN_REMOVAL_REPORT, icon: <ReportProblemIcon fontSize="small" />, type: 'GARDEN_REMOVAL_REPORT' },
  { key: 'PHOTO_SUGGESTION', label: TYPE_LABELS.PHOTO_SUGGESTION, icon: <ImageIcon fontSize="small" />, type: 'PHOTO_SUGGESTION' },
  { key: 'GARDEN_LOCATION_SUGGESTION', label: TYPE_LABELS.GARDEN_LOCATION_SUGGESTION, icon: <EditLocationIcon fontSize="small" />, type: 'GARDEN_LOCATION_SUGGESTION' },
  { key: 'POST_REPORT', label: TYPE_LABELS.POST_REPORT, icon: <ReportIcon fontSize="small" />, type: 'POST_REPORT' },
  { key: 'PROVIDER_APPLICATION', label: TYPE_LABELS.PROVIDER_APPLICATION, icon: <StoreIcon fontSize="small" />, type: 'PROVIDER_APPLICATION' },
  { key: 'PROVIDER_PUBLIC_APPLICATION', label: TYPE_LABELS.PROVIDER_PUBLIC_APPLICATION, icon: <StoreIcon fontSize="small" />, type: 'PROVIDER_PUBLIC_APPLICATION' },
];

const STAT_CARDS = [
  { key: 'GARDEN_REQUEST', label: 'Gardens', countKey: 'gardens', icon: <ParkIcon /> },
  { key: 'GARDEN_REMOVAL_REPORT', label: 'Removals', countKey: 'gardenRemovalReports', icon: <ReportProblemIcon /> },
  { key: 'PHOTO_SUGGESTION', label: 'Photos', countKey: 'photos', icon: <ImageIcon /> },
  { key: 'GARDEN_LOCATION_SUGGESTION', label: 'Locations', countKey: 'locationSuggestions', icon: <EditLocationIcon /> },
  { key: 'POST_REPORT', label: 'Reports', countKey: 'reports', icon: <ReportIcon /> },
  { key: 'PROVIDER_APPLICATION', label: 'Providers', countKey: 'providers', icon: <StoreIcon /> },
  { key: 'PROVIDER_PUBLIC_APPLICATION', label: 'Public apps', countKey: 'publicProviderApplications', icon: <StoreIcon /> },
];

// Which resolutions are valid per submission type — drives which buttons render.
const ALLOWED_RESOLUTIONS = {
  GARDEN_REQUEST: ['APPROVED', 'REJECTED'],
  GARDEN_REMOVAL_REPORT: ['APPROVED', 'DISMISSED'],
  PHOTO_SUGGESTION: ['APPROVED', 'REJECTED'],
  GARDEN_LOCATION_SUGGESTION: ['APPROVED', 'REJECTED'],
  POST_REPORT: ['APPROVED', 'DISMISSED'],
  PROVIDER_APPLICATION: ['APPROVED', 'REJECTED'],
  // DISMISSED is deliberately absent — the backend throws IllegalArgumentException for it on this type.
  PROVIDER_PUBLIC_APPLICATION: ['APPROVED', 'REJECTED'],
};

// Default verbs per resolution; per-type overrides below.
const RESOLUTION_LABELS = {
  APPROVED: 'Approve',
  REJECTED: 'Reject',
  DISMISSED: 'Dismiss',
};

// Per-(type, resolution) button label override. Falls back to RESOLUTION_LABELS.
const TYPE_RESOLUTION_LABELS = {
  GARDEN_REMOVAL_REPORT: {
    APPROVED: 'Confirm — keep hidden',
    DISMISSED: 'Dismiss — restore garden',
  },
};

const resolutionLabel = (type, resolution) =>
  TYPE_RESOLUTION_LABELS[type]?.[resolution] ?? RESOLUTION_LABELS[resolution] ?? resolution;

const RESOLUTION_COLORS = {
  APPROVED: 'success',
  REJECTED: 'error',
  DISMISSED: 'warning',
};

const RESOLUTION_ICONS = {
  APPROVED: <ApproveIcon fontSize="small" />,
  REJECTED: <RejectIcon fontSize="small" />,
  DISMISSED: <DismissIcon fontSize="small" />,
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const TargetCell = ({ submission, onResend, onClaimForUser }) => {
  const target = submission.target || {};
  switch (target.kind) {
    case 'GARDEN_REQUEST':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {target.previewPhotoUrl ? (
            <Box
              component="img"
              src={normalizeImageUrl(target.previewPhotoUrl)}
              alt={target.name}
              sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', bgcolor: 'grey.100' }}
            />
          ) : (
            <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ParkIcon color="disabled" />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Link component={RouterLink} to={`/dog-gardens/${target.gardenId}`} underline="hover">
              <Typography variant="body2" fontWeight={600} noWrap>{target.name}</Typography>
            </Link>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[target.city, target.country].filter(Boolean).join(', ') || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {target.address}
            </Typography>
          </Box>
        </Stack>
      );

    case 'GARDEN_REMOVAL_REPORT':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {target.previewPhotoUrl ? (
            <Box
              component="img"
              src={normalizeImageUrl(target.previewPhotoUrl)}
              alt={target.gardenName}
              sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', bgcolor: 'grey.100' }}
            />
          ) : (
            <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReportProblemIcon color="disabled" />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Link component={RouterLink} to={`/dog-gardens/${target.gardenId}`} underline="hover">
                <Typography variant="body2" fontWeight={600} noWrap>{target.gardenName}</Typography>
              </Link>
              {target.totalOpenReports > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${target.totalOpenReports} reports`}
                  sx={{ height: 18 }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[target.city, target.country].filter(Boolean).join(', ') || target.address || '—'}
            </Typography>
            <Tooltip title={target.note || ''} placement="top-start">
              <Typography variant="caption" color="text.secondary" component="div" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                reason: {target.reason}{target.note ? ` — "${target.note}"` : ''}
              </Typography>
            </Tooltip>
          </Box>
        </Stack>
      );

    case 'PHOTO_SUGGESTION':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={normalizeImageUrl(target.photoUrl)}
            alt="suggested photo"
            sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', bgcolor: 'grey.100' }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>Suggested photo</Typography>
            <Link component={RouterLink} to={`/dog-gardens/${target.gardenId}`} underline="hover">
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                for: {target.gardenName}
              </Typography>
            </Link>
          </Box>
        </Stack>
      );

    case 'GARDEN_LOCATION_SUGGESTION':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EditLocationIcon color="disabled" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Link component={RouterLink} to={`/dog-gardens/${target.gardenId}`} underline="hover">
              <Typography variant="body2" fontWeight={600} noWrap>{target.gardenName}</Typography>
            </Link>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              current:{' '}
              <Link
                href={`https://www.google.com/maps?q=${target.currentLatitude},${target.currentLongitude}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {target.currentLatitude?.toFixed(6)}, {target.currentLongitude?.toFixed(6)}
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              suggested:{' '}
              <Link
                href={`https://www.google.com/maps?q=${target.suggestedLatitude},${target.suggestedLongitude}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {target.suggestedLatitude?.toFixed(6)}, {target.suggestedLongitude?.toFixed(6)}
              </Link>
            </Typography>
          </Box>
        </Stack>
      );

    case 'POST_REPORT':
      return (
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={target.reason || ''} placement="top-start">
            <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
              Reason: {target.reason}
            </Typography>
          </Tooltip>
          <Link component={RouterLink} to={`/posts/${target.postId}`} underline="hover">
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              post #{target.postId}
              {target.postAuthorName ? ` by ${target.postAuthorName}` : ''}
            </Typography>
          </Link>
          {target.postExcerpt && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320, fontStyle: 'italic' }}>
              "{target.postExcerpt}"
            </Typography>
          )}
        </Box>
      );

    case 'PROVIDER_APPLICATION':
      return (
        <Box sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`/providers/${target.providerId}`} underline="hover">
            <Typography variant="body2" fontWeight={600} noWrap>{target.title || '(untitled provider)'}</Typography>
          </Link>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            owner: {target.ownerName || '—'}
          </Typography>
          {target.serviceTypes?.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {target.serviceTypes.map((s) => (
                <Chip key={s} label={s} size="small" variant="outlined" />
              ))}
            </Stack>
          )}
        </Box>
      );

    case 'PROVIDER_PUBLIC_APPLICATION':
      return (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>{target.businessName}</Typography>
          <Typography variant="caption">
            {target.contactName} · {target.contactEmail} · {target.contactPhone}
          </Typography>
          <Typography variant="caption">{target.city}</Typography>
          <Typography variant="caption">{(target.serviceTypes || []).join(', ')}</Typography>
          {target.about && <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{target.about}</Typography>}
          {target.matchedExistingUser && (
            <Chip
              size="small"
              color={target.matchedUserHasProvider ? 'warning' : 'default'}
              label={target.matchedUserHasProvider
                ? 'This email already has a provider listing'
                : 'This email already has an Anipaw account'}
            />
          )}
          {target.submittedIp && (
            <Typography variant="caption" color="text.secondary">IP {target.submittedIp}</Typography>
          )}
          {target.approved && !target.claimed && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => onResend?.(target.applicationId)}>
                Re-send claim link
              </Button>
              <Button size="small" variant="outlined" onClick={() => onClaimForUser?.(target.applicationId)}>
                Link to a user…
              </Button>
            </Stack>
          )}
          {target.claimed && <Chip size="small" color="success" label="Claimed" />}
        </Stack>
      );

    case 'MISSING':
    default:
      return (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">
          target deleted (id {target.targetId ?? '?'})
        </Typography>
      );
  }
};

const Submissions = () => {
  const navigate = useNavigate();
  const api = useApi();

  // Deep-linked from /providers and /provider-claims. The URL is the source of truth in BOTH
  // directions: reading it only at mount would ignore a deep link followed while already on this
  // page (React reuses the mounted component), and not writing it back would leave a stale ?tab=
  // that reopens the wrong tab on refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = TAB_DEFS.some((t) => t.key === requestedTab) ? requestedTab : 'ALL';
  const setActiveTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key && key !== 'ALL') next.set('tab', key); else next.delete('tab');
    // replace: switching tabs is navigation within one view, not a step to go Back through.
    setSearchParams(next, { replace: true });
  };
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [counts, setCounts] = useState({ gardens: 0, gardenRemovalReports: 0, photos: 0, locationSuggestions: 0, reports: 0, providers: 0, publicProviderApplications: 0, total: 0 });

  // Reject / dismiss dialog state
  const [actionDialog, setActionDialog] = useState({
    open: false,
    submissionId: null,
    resolution: null,  // 'REJECTED' | 'DISMISSED'
    note: '',
  });
  const [resolvingId, setResolvingId] = useState(null);

  // Public provider-application claim escape hatches
  const [claimForUserTarget, setClaimForUserTarget] = useState(null);
  const [claimForUserId, setClaimForUserId] = useState('');

  const activeType = useMemo(
    () => TAB_DEFS.find((t) => t.key === activeTab)?.type ?? null,
    [activeTab]
  );

  const fetchCounts = useCallback(async () => {
    try {
      const data = await api.moderation.getCounts();
      setCounts(data || { gardens: 0, gardenRemovalReports: 0, photos: 0, locationSuggestions: 0, reports: 0, providers: 0, publicProviderApplications: 0, total: 0 });
    } catch (e) {
      // counts are non-blocking; just log
      console.error('counts failed', e);
    }
  }, [api]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.moderation.listSubmissions({
        status: statusFilter === 'ALL' ? null : statusFilter,
        type: activeType,
        page,
        size: rowsPerPage,
      });
      setRows(data?.content || []);
      setTotalElements(data?.totalElements || 0);
      setError('');
    } catch (e) {
      console.error(e);
      setError('Failed to load submissions.');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, activeType, page, rowsPerPage]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeTab, statusFilter]);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleApprove = async (sub) => {
    setResolvingId(sub.id);
    try {
      await api.moderation.resolve(sub.id, 'APPROVED', null);
      flashSuccess(`${resolutionLabel(sub.type, 'APPROVED')} — submission #${sub.id}`);
      fetchRows();
      fetchCounts();
    } catch (e) {
      console.error(e);
      setError(`Failed to approve: ${e.message}`);
    } finally {
      setResolvingId(null);
    }
  };

  const handleResend = async (applicationId) => {
    try {
      await api.providerApplication.resendClaimLink(applicationId);
      // Always mints a fresh link and invalidates the previous one -- the raw token is
      // never stored, so re-sending the original is impossible by design.
      flashSuccess('A new claim link has been sent. Any earlier link no longer works.');
      await fetchRows();
      await fetchCounts();
    } catch (e) {
      // The backend sends synchronously and rolls back on failure, so a failure here
      // means the previous link is still valid and nothing changed.
      console.error(e);
      setError('Could not send the claim email. The previous link is unchanged.');
    }
  };

  const handleClaimForUser = async (applicationId, userId) => {
    try {
      await api.providerApplication.claimForUser(applicationId, Number(userId));
      flashSuccess('Listing linked to that user.');
      setClaimForUserTarget(null);
      await fetchRows();
      await fetchCounts();
    } catch (e) {
      console.error(e);
      setError('Could not link the listing. Check the user id and that they do not already have one.');
    }
  };

  const openActionDialog = (sub, resolution) => {
    setActionDialog({ open: true, submissionId: sub.id, type: sub.type, resolution, note: '' });
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, submissionId: null, type: null, resolution: null, note: '' });
  };

  const handleDialogConfirm = async () => {
    const { submissionId, type, resolution, note } = actionDialog;
    if (!submissionId || !resolution) return;
    setResolvingId(submissionId);
    try {
      await api.moderation.resolve(submissionId, resolution, note?.trim() || null);
      flashSuccess(`${resolutionLabel(type, resolution)} — submission #${submissionId}`);
      closeActionDialog();
      fetchRows();
      fetchCounts();
    } catch (e) {
      console.error(e);
      setError(`Failed: ${e.message}`);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">Submissions</Typography>
          <Typography variant="body2" color="text.secondary">
            User submissions awaiting admin review.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="SUBMITTED">Pending</MenuItem>
            <MenuItem value="RESOLVED">Resolved</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CARDS.map((card) => (
          <Grid item xs={6} sm={3} key={card.key}>
            <Card
              variant={activeTab === card.key ? 'elevation' : 'outlined'}
              elevation={activeTab === card.key ? 4 : 0}
              sx={{ cursor: 'pointer', borderColor: activeTab === card.key ? 'primary.main' : undefined }}
              onClick={() => setActiveTab(card.key)}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: 'primary.main' }}>{card.icon}</Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h5">{counts[card.countKey] ?? 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {TAB_DEFS.map((tab) => {
            const tabCount =
              tab.key === 'ALL' ? counts.total :
              tab.key === 'GARDEN_REQUEST' ? counts.gardens :
              tab.key === 'GARDEN_REMOVAL_REPORT' ? counts.gardenRemovalReports :
              tab.key === 'PHOTO_SUGGESTION' ? counts.photos :
              tab.key === 'GARDEN_LOCATION_SUGGESTION' ? counts.locationSuggestions :
              tab.key === 'POST_REPORT' ? counts.reports :
              tab.key === 'PROVIDER_APPLICATION' ? counts.providers :
              tab.key === 'PROVIDER_PUBLIC_APPLICATION' ? counts.publicProviderApplications :
              0;
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                icon={tab.icon}
                iconPosition="start"
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{tab.label}</span>
                    {tabCount > 0 && (
                      <Chip label={tabCount} size="small" color="primary" sx={{ height: 18 }} />
                    )}
                  </Stack>
                }
                sx={{ minHeight: 48, textTransform: 'none' }}
              />
            );
          })}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <InboxIcon sx={{ fontSize: 56, opacity: 0.3, mb: 1 }} />
            <Typography>No submissions in this view.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((sub) => {
                  const allowed = ALLOWED_RESOLUTIONS[sub.type] || [];
                  const isPending = sub.status === 'SUBMITTED';
                  const busy = resolvingId === sub.id;
                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{formatDate(sub.createdAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={TYPE_LABELS[sub.type] || sub.type}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {sub.submittedBy ? (
                          <Link component={RouterLink} to={`/users/${sub.submittedBy.id}`} underline="hover">
                            <Typography variant="body2">{sub.submittedBy.name}</Typography>
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.disabled">anonymous</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TargetCell
                          submission={sub}
                          onResend={handleResend}
                          onClaimForUser={setClaimForUserTarget}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isPending ? (
                          <Chip size="small" label="Pending" color="default" />
                        ) : (
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              label={sub.resolution || 'RESOLVED'}
                              color={RESOLUTION_COLORS[sub.resolution] || 'default'}
                            />
                            {sub.resolvedBy && (
                              <Typography variant="caption" color="text.secondary">
                                by {sub.resolvedBy.name} · {formatDate(sub.resolvedAt)}
                              </Typography>
                            )}
                            {sub.resolutionNote && (
                              <Tooltip title={sub.resolutionNote}><Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>note: {sub.resolutionNote}</Typography></Tooltip>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isPending && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {allowed.includes('APPROVED') && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={RESOLUTION_ICONS.APPROVED}
                                disabled={busy}
                                onClick={() => handleApprove(sub)}
                              >
                                {resolutionLabel(sub.type, 'APPROVED')}
                              </Button>
                            )}
                            {allowed.includes('REJECTED') && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={RESOLUTION_ICONS.REJECTED}
                                disabled={busy}
                                onClick={() => openActionDialog(sub, 'REJECTED')}
                              >
                                {resolutionLabel(sub.type, 'REJECTED')}
                              </Button>
                            )}
                            {allowed.includes('DISMISSED') && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={RESOLUTION_ICONS.DISMISSED}
                                disabled={busy}
                                onClick={() => openActionDialog(sub, 'DISMISSED')}
                              >
                                {resolutionLabel(sub.type, 'DISMISSED')}
                              </Button>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.resolution ? resolutionLabel(actionDialog.type, actionDialog.resolution) : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionDialog.type === 'GARDEN_REMOVAL_REPORT' && actionDialog.resolution === 'DISMISSED'
              ? 'Dismissing closes the open reports and restores the garden to visible=true (the auto-hide was a false positive).'
              : actionDialog.resolution === 'REJECTED'
                ? 'Rejecting will permanently delete the submitted item.'
                : 'Dismissing closes the submission without taking action.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Note (optional)"
            placeholder="Internal note recorded with this resolution"
            value={actionDialog.note}
            onChange={(e) => setActionDialog((d) => ({ ...d, note: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            color={RESOLUTION_COLORS[actionDialog.resolution] || 'primary'}
          >
            {actionDialog.resolution ? resolutionLabel(actionDialog.type, actionDialog.resolution) : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={claimForUserTarget !== null} onClose={() => setClaimForUserTarget(null)}>
        <DialogTitle>Link this listing to a user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Last resort, for an applicant who signed up under a different address and lost
            their claim link. This creates their provider listing immediately.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="User id"
            value={claimForUserId}
            onChange={(e) => setClaimForUserId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimForUserTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!claimForUserId}
            onClick={() => {
              handleClaimForUser(claimForUserTarget, claimForUserId);
              setClaimForUserId('');
            }}
          >
            Link listing
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Submissions;
